import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { STATIC_BASE, formatApiError } from "@/lib/api";
import {
  LayoutDashboard, ShoppingCart, Music, Users, Settings, Sparkles,
  Plus, Trash2, Upload, RefreshCw, Mail, ClipboardList, Copy, Check,
  FileText,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "projects", label: "Projects", icon: FileText },
  { key: "orders", label: "Legacy Orders", icon: ShoppingCart },
  { key: "samples", label: "Samples", icon: Music },
  { key: "users", label: "Users", icon: Users },
  { key: "contacts", label: "Contacts", icon: Mail },
  { key: "lyrics", label: "AI Lyrics", icon: Sparkles },
  { key: "settings", label: "Settings", icon: Settings },
];

const STATUSES = [
  "Pending", "Discovery", "Writing Lyrics", "Composing", "Recording",
  "Mixing", "Mastering", "Client Review", "Approved",
  "Delivered", "Completed",
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  return (
    <div className="bg-black min-h-[calc(100vh-80px)]">
      <div className="container-x py-10">
        <div className="mb-8">
          <div className="eyebrow mb-2">Admin Console</div>
          <h1 className="font-heading text-3xl md:text-4xl">Songniture Control Center</h1>
          <p className="text-white/60 mt-2">Welcome, {user?.name}.</p>
        </div>

        <div className="border-b border-ink-700 mb-8 flex overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-semibold whitespace-nowrap border-b-2 transition flex items-center gap-2 ${
                tab === t.key ? "text-gold border-gold" : "text-white/55 border-transparent hover:text-gold"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "samples" && <SamplesTab />}
        {tab === "users" && <UsersTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "lyrics" && <LyricsTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

function StatBox({ value, label }) {
  return (
    <div className="bg-ink-900 border border-ink-700 p-6">
      <div className="font-heading text-3xl text-gold">{value}</div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-white/55 font-semibold mt-1">{label}</div>
    </div>
  );
}

function OverviewTab() {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/orders"),
      api.get("/admin/users"),
      api.get("/admin/contacts"),
    ]).then(([o, u, c]) => { setOrders(o.data); setUsers(u.data); setContacts(c.data); });
  }, []);

  const revenue = orders.reduce((s, o) => {
    return s + (o.purchased_variation_ids?.length || 0) * (o.estimated_total ? o.estimated_total / o.variations_promised : 0);
  }, 0);

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      <StatBox value={orders.length} label="Total Orders" />
      <StatBox value={users.filter((u) => u.role === "customer").length} label="Customers" />
      <StatBox value={contacts.length} label="Contact Submissions" />
      <StatBox value={`$${revenue.toFixed(0)}`} label="Realized Revenue" />
    </div>
  );
}

function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [opened, setOpened] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/projects");
    setProjects(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl">All Projects (New Workflow)</h2>
        <button onClick={load} className="btn-outline"><RefreshCw size={14} /> Refresh</button>
      </div>
      {projects.length === 0 ? (
        <div className="text-white/50">No projects yet.</div>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => (
            <ProjectRow key={p.id} project={p} opened={opened === p.id} setOpened={(v) => setOpened(v ? p.id : null)} reload={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectRow({ project, opened, setOpened, reload }) {
  const bp = project.brief?.business_profile || {};
  return (
    <div className="bg-ink-900 border border-ink-700" data-testid={`admin-project-${project.id}`}>
      <button onClick={() => setOpened(!opened)} className="w-full text-left p-5 flex justify-between items-center flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-semibold">{project.status.replace(/_/g, " ")}</div>
          <div className="font-heading text-lg mt-0.5">{bp.business_name || "Untitled"}</div>
          <div className="text-white/55 text-xs">{project.customer_email} • {new Date(project.created_at).toLocaleString()}</div>
        </div>
        <div className="text-right">
          <div className="text-gold font-heading">${(project.total_paid || 0).toFixed(0)}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">Paid</div>
        </div>
      </button>
      {opened && (
        <div className="border-t border-ink-700 p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="eyebrow">Slogan:</span> <span className="text-white/85 ml-2">{bp.slogan || "—"}</span></div>
            <div><span className="eyebrow">Website:</span> <span className="text-white/85 ml-2">{bp.website || "—"}</span></div>
            <div><span className="eyebrow">Phone:</span> <span className="text-white/85 ml-2">{bp.phone || "—"}</span></div>
            <div><span className="eyebrow">Years In Business:</span> <span className="text-white/85 ml-2">{bp.years_in_business || "—"}</span></div>
            <div className="sm:col-span-2"><span className="eyebrow">Mission:</span> <span className="text-white/85 ml-2">{bp.mission || "—"}</span></div>
          </div>

          {project.lyrics_final && (
            <details className="bg-black border border-ink-700">
              <summary className="cursor-pointer p-4 eyebrow">View Submitted Lyrics</summary>
              <pre className="p-4 whitespace-pre-wrap text-white/85 text-xs font-mono max-h-96 overflow-auto">{project.lyrics_final}</pre>
            </details>
          )}

          <div>
            <div className="eyebrow mb-3">Song Variations ({project.variations?.length || 0} / 3)</div>
            <div className="space-y-3">
              {project.variations?.map((v) => (
                <div key={v.id} className="bg-black border border-ink-700 p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-white font-semibold">{v.title}</div>
                    <audio controls preload="none" src={`${STATIC_BASE}${v.audio_url}`} />
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Delete this variation?")) return;
                      await api.delete(`/admin/projects/${project.id}/variations/${v.id}`);
                      reload();
                    }}
                    className="text-destructive hover:text-red-300"
                    data-testid={`admin-proj-delete-var-${v.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <ProjectVariationUploader projectId={project.id} onUploaded={reload} />
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectVariationUploader({ projectId, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", creative_notes: "" });
  const [audio, setAudio] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async () => {
    if (!audio || !form.title) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("creative_notes", form.creative_notes);
      fd.append("audio", audio);
      await api.post(`/admin/projects/${projectId}/variations`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ title: "", description: "", creative_notes: "" });
      setAudio(null);
      setOpen(false);
      onUploaded();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-outline" data-testid={`admin-proj-add-var-${projectId}`}>
          <Plus size={14} /> Upload Song Version
        </button>
      ) : (
        <div className="bg-black border border-gold/30 p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="input" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="proj-upload-var-title" />
            <input className="input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input className="input sm:col-span-2" placeholder="Creative notes" value={form.creative_notes} onChange={(e) => setForm({ ...form, creative_notes: e.target.value })} />
          </div>
          <div>
            <label className="label">Audio File *</label>
            <input type="file" accept="audio/*" onChange={(e) => setAudio(e.target.files?.[0])} className="text-white/70 text-sm" data-testid="proj-upload-var-audio" />
          </div>
          {err && <div className="text-destructive text-sm">{err}</div>}
          <div className="flex gap-3">
            <button onClick={upload} disabled={busy || !audio || !form.title} className="btn-gold" data-testid="proj-upload-var-submit">
              <Upload size={14} /> {busy ? "Uploading…" : "Upload"}
            </button>
            <button onClick={() => setOpen(false)} className="btn-outline">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [opened, setOpened] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    const { data } = await api.get("/admin/orders");
    setOrders(data);
    setRefreshing(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-heading text-2xl">All Orders</h2>
        <button onClick={load} className="btn-outline" data-testid="admin-orders-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>
      {orders.length === 0 ? (
        <div className="text-white/50">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <OrderRow key={o.id} order={o} opened={opened === o.id} setOpened={(v) => setOpened(v ? o.id : null)} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderRow({ order, opened, setOpened, onChange }) {
  const [busy, setBusy] = useState(false);

  const setStatus = async (s) => {
    const fd = new FormData();
    fd.append("status_val", s);
    await api.put(`/admin/orders/${order.id}/status`, fd);
    onChange();
  };

  return (
    <div className="bg-ink-900 border border-ink-700" data-testid={`admin-order-${order.id}`}>
      <button onClick={() => setOpened(!opened)} className="w-full text-left p-5 flex justify-between items-center flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-semibold">{order.package_name}</div>
          <div className="font-heading text-lg">{order.questionnaire?.company_name}</div>
          <div className="text-white/55 text-xs">{order.customer_email} • {new Date(order.created_at).toLocaleString()}</div>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={order.status}
            onChange={(e) => { e.stopPropagation(); setStatus(e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            className="input !py-2 !px-3 !text-xs w-44"
            data-testid={`admin-order-status-${order.id}`}
          >
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <div className="text-gold font-heading text-lg">${order.estimated_total?.toFixed(0)}</div>
        </div>
      </button>
      {opened && (
        <div className="border-t border-ink-700 p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="eyebrow">Industry:</span> <span className="text-white/80 ml-2">{order.questionnaire?.industry}</span></div>
            <div><span className="eyebrow">Style:</span> <span className="text-white/80 ml-2">{order.questionnaire?.music_style}</span></div>
            <div className="sm:col-span-2"><span className="eyebrow">Brand Message:</span> <span className="text-white/80 ml-2">{order.questionnaire?.brand_message}</span></div>
            <div className="sm:col-span-2"><span className="eyebrow">Target Audience:</span> <span className="text-white/80 ml-2">{order.questionnaire?.target_audience}</span></div>
            {order.questionnaire?.notes && <div className="sm:col-span-2"><span className="eyebrow">Notes:</span> <span className="text-white/80 ml-2">{order.questionnaire?.notes}</span></div>}
          </div>

          <div>
            <div className="eyebrow mb-3">Delivered Variations ({order.variations?.length || 0} / {order.variations_promised})</div>
            <div className="space-y-3">
              {order.variations?.map((v) => (
                <div key={v.id} className="bg-black border border-ink-700 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-variation-${v.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-white font-semibold">{v.title}</div>
                    <audio controls preload="none" src={`${STATIC_BASE}${v.audio_url}`} />
                  </div>
                  <button
                    onClick={async () => {
                      await api.delete(`/admin/orders/${order.id}/variations/${v.id}`);
                      onChange();
                    }}
                    className="text-destructive hover:text-red-300"
                    data-testid={`admin-delete-variation-${v.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <VariationUploader orderId={order.id} onUploaded={onChange} />
          </div>
        </div>
      )}
    </div>
  );
}

function VariationUploader({ orderId, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", creative_notes: "", marketing_purpose: "" });
  const [audio, setAudio] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async () => {
    if (!audio || !form.title) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("creative_notes", form.creative_notes);
      fd.append("marketing_purpose", form.marketing_purpose);
      fd.append("audio", audio);
      await api.post(`/admin/orders/${orderId}/variations`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ title: "", description: "", creative_notes: "", marketing_purpose: "" });
      setAudio(null);
      setOpen(false);
      onUploaded();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4">
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-outline" data-testid={`admin-add-variation-${orderId}`}>
          <Plus size={14} /> Upload Variation
        </button>
      ) : (
        <div className="bg-black border border-gold/30 p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="input" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="upload-var-title" />
            <input className="input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="upload-var-desc" />
            <input className="input" placeholder="Creative notes" value={form.creative_notes} onChange={(e) => setForm({ ...form, creative_notes: e.target.value })} data-testid="upload-var-notes" />
            <input className="input" placeholder="Marketing purpose" value={form.marketing_purpose} onChange={(e) => setForm({ ...form, marketing_purpose: e.target.value })} data-testid="upload-var-purpose" />
          </div>
          <div>
            <label className="label">Audio File *</label>
            <input type="file" accept="audio/*" onChange={(e) => setAudio(e.target.files?.[0])} className="text-white/70 text-sm" data-testid="upload-var-audio" />
          </div>
          {err && <div className="text-destructive text-sm">{err}</div>}
          <div className="flex gap-3">
            <button onClick={upload} disabled={busy || !audio || !form.title} className="btn-gold" data-testid="upload-var-submit">
              <Upload size={14} /> {busy ? "Uploading…" : "Upload"}
            </button>
            <button onClick={() => setOpen(false)} className="btn-outline">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SamplesTab() {
  const [samples, setSamples] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = { title: "", industry: "", description: "", audio_url: "", image_url: "", order_idx: 0 };

  const load = useCallback(() => api.get("/samples").then((r) => setSamples(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (s) => {
    if (s.id) {
      await api.put(`/admin/samples/${s.id}`, s);
    } else {
      await api.post("/admin/samples", s);
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/samples/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-between mb-5">
        <h2 className="font-heading text-2xl">Sample Library</h2>
        <button onClick={() => setEditing(blank)} className="btn-gold" data-testid="admin-add-sample"><Plus size={14} /> New Sample</button>
      </div>
      {editing && <SampleEditor sample={editing} onSave={save} onCancel={() => setEditing(null)} />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
        {samples.map((s) => (
          <div key={s.id} className="bg-ink-900 border border-ink-700 p-5">
            <div className="aspect-video overflow-hidden bg-black mb-3">
              {s.image_url && <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" />}
            </div>
            <div className="text-gold text-[10px] tracking-[0.25em] uppercase font-semibold">{s.industry}</div>
            <div className="font-heading text-lg">{s.title}</div>
            <div className="text-white/55 text-sm line-clamp-2 mb-3">{s.description}</div>
            <audio controls preload="none" src={s.audio_url} className="mb-3" />
            <div className="flex gap-2">
              <button onClick={() => setEditing(s)} className="btn-outline !py-2 !px-4 !text-xs">Edit</button>
              <button onClick={() => remove(s.id)} className="text-destructive hover:text-red-300"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SampleEditor({ sample, onSave, onCancel }) {
  const [s, setS] = useState(sample);
  return (
    <div className="bg-ink-900 border border-gold/30 p-5 space-y-3" data-testid="sample-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        <input className="input" placeholder="Title" value={s.title} onChange={(e) => setS({ ...s, title: e.target.value })} data-testid="sample-title" />
        <input className="input" placeholder="Industry" value={s.industry} onChange={(e) => setS({ ...s, industry: e.target.value })} data-testid="sample-industry" />
        <input className="input sm:col-span-2" placeholder="Description" value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} data-testid="sample-desc" />
        <input className="input" placeholder="Audio URL (mp3)" value={s.audio_url} onChange={(e) => setS({ ...s, audio_url: e.target.value })} data-testid="sample-audio-url" />
        <input className="input" placeholder="Image URL" value={s.image_url} onChange={(e) => setS({ ...s, image_url: e.target.value })} data-testid="sample-image-url" />
        <input type="number" className="input" placeholder="Order" value={s.order_idx} onChange={(e) => setS({ ...s, order_idx: Number(e.target.value) })} />
      </div>
      <div className="flex gap-3">
        <button onClick={() => onSave(s)} className="btn-gold" data-testid="sample-save">Save</button>
        <button onClick={onCancel} className="btn-outline">Cancel</button>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div>
      <h2 className="font-heading text-2xl mb-5">All Users</h2>
      <div className="bg-ink-900 border border-ink-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-700 text-left">
              <th className="p-4 eyebrow">Name</th>
              <th className="p-4 eyebrow">Email</th>
              <th className="p-4 eyebrow">Company</th>
              <th className="p-4 eyebrow">Role</th>
              <th className="p-4 eyebrow">Created</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-ink-700/50">
                <td className="p-4 text-white">{u.name}</td>
                <td className="p-4 text-white/80">{u.email}</td>
                <td className="p-4 text-white/60">{u.company}</td>
                <td className="p-4"><span className={u.role === "admin" ? "text-gold" : "text-white/70"}>{u.role}</span></td>
                <td className="p-4 text-white/50 text-xs">{new Date(u.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  useEffect(() => { api.get("/admin/contacts").then((r) => setContacts(r.data)); }, []);
  return (
    <div>
      <h2 className="font-heading text-2xl mb-5">Contact Submissions</h2>
      {contacts.length === 0 ? <div className="text-white/50">No submissions yet.</div> : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div key={c.id} className="bg-ink-900 border border-ink-700 p-5">
              <div className="flex justify-between flex-wrap mb-2">
                <div>
                  <div className="text-white font-semibold">{c.name}</div>
                  <div className="text-gold text-sm">{c.email} • {c.phone}</div>
                </div>
                <div className="text-white/50 text-xs">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              {c.company && <div className="text-white/65 text-sm mb-1">Company: {c.company}</div>}
              {c.subject && <div className="text-white/65 text-sm mb-2">Subject: {c.subject}</div>}
              <div className="text-white/80 text-sm whitespace-pre-wrap">{c.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LyricsTab() {
  const [form, setForm] = useState({
    company_name: "", industry: "", brand_message: "", target_audience: "",
    music_style: "", tone: "uplifting and memorable",
  });
  const [lyrics, setLyrics] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setBusy(true); setErr(""); setLyrics("");
    try {
      const { data } = await api.post("/admin/lyrics/generate", form);
      setLyrics(data.lyrics);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(lyrics);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl">AI Lyric Generator</h2>
        <p className="text-white/55 text-sm">Powered by Claude Sonnet 4.5. Use this to draft songwriting starting points.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-ink-900 border border-ink-700 p-6 space-y-4">
          <input className="input" placeholder="Company Name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="lyrics-company" />
          <input className="input" placeholder="Industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="lyrics-industry" />
          <textarea rows={3} className="input resize-none" placeholder="Brand Message" value={form.brand_message} onChange={(e) => setForm({ ...form, brand_message: e.target.value })} data-testid="lyrics-message" />
          <input className="input" placeholder="Target Audience" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} data-testid="lyrics-audience" />
          <input className="input" placeholder="Music Style (e.g. cinematic pop)" value={form.music_style} onChange={(e) => setForm({ ...form, music_style: e.target.value })} data-testid="lyrics-style" />
          <input className="input" placeholder="Tone" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} data-testid="lyrics-tone" />
          <button onClick={generate} disabled={busy || !form.company_name} className="btn-gold w-full justify-center" data-testid="lyrics-generate">
            <Sparkles size={14} /> {busy ? "Generating…" : "Generate Lyrics"}
          </button>
          {err && <div className="text-destructive text-sm" data-testid="lyrics-error">{err}</div>}
        </div>
        <div className="bg-ink-900 border border-ink-700 p-6 relative min-h-[300px]">
          {lyrics ? (
            <>
              <button onClick={copy} className="absolute top-4 right-4 text-gold hover:text-gold-light flex items-center gap-1 text-xs" data-testid="lyrics-copy">
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
              <pre className="whitespace-pre-wrap font-body text-white/85 text-sm leading-relaxed" data-testid="lyrics-output">{lyrics}</pre>
            </>
          ) : (
            <div className="text-white/40 italic flex items-center justify-center h-full">Your generated lyrics will appear here.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsField({ field, label, settings, setSettings }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" value={settings[field] || ""} onChange={(e) => setSettings({ ...settings, [field]: e.target.value })} data-testid={`setting-${field}`} />
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  if (!settings) return <div className="text-white/50">Loading…</div>;

  const save = async () => {
    setBusy(true); setDone(false);
    await api.put("/admin/settings", settings);
    setBusy(false); setDone(true);
    setTimeout(() => setDone(false), 2000);
  };

  const F = ({ field, label }) => (
    <SettingsField field={field} label={label} settings={settings} setSettings={setSettings} />
  );

  return (
    <div className="max-w-3xl">
      <h2 className="font-heading text-2xl mb-5">Branding & Business Settings</h2>
      <div className="bg-ink-900 border border-ink-700 p-6 space-y-5">
        <F field="business_name" label="Business Name" />
        <F field="slogan" label="Primary Slogan" />
        <F field="tagline" label="Founder Tagline" />
        <F field="hero_subheadline" label="Hero Subheadline" />
        <div className="grid sm:grid-cols-2 gap-5">
          <F field="founder_name" label="Founder Name" />
          <F field="founder_title" label="Founder Title" />
        </div>
        <F field="founder_photo" label="Founder Photo URL" />
        <div className="grid sm:grid-cols-3 gap-5">
          <F field="phone" label="Phone" />
          <F field="email" label="Email" />
          <F field="website" label="Website" />
        </div>
        <div className="flex items-center gap-4 pt-3">
          <button onClick={save} disabled={busy} className="btn-gold" data-testid="settings-save">
            {busy ? "Saving…" : "Save Settings"}
          </button>
          {done && <span className="text-green-400 text-sm">Saved ✓</span>}
        </div>
      </div>
    </div>
  );
}
